import React, { useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import { formatRupiah } from '../utils/formatter';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const Laporan = () => {
  const [activeTab, setActiveTab] = useState('gabungan');
  const [laporan, setLaporan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filterType, setFilterType] = useState('daily');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchLaporan = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/peminjaman/laporan`, {
        params: {
          startDate,
          endDate
        }
      });
      setLaporan(response.data);
      setCurrentPage(1);
    } catch (error) {
      Swal.fire('Gagal', 'Gagal memuat data laporan. Pastikan tanggal telah diisi.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    const today = new Date();
    let start = '';
    let end = '';

    if (type === 'daily') {
      start = today.toISOString().split('T')[0];
      end = today.toISOString().split('T')[0];
    } else if (type === 'monthly') {
      start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    } else if (type === 'yearly') {
      start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
      end = new Date(today.getFullYear(), 11, 31).toISOString().split('T')[0];
    }

    setStartDate(start);
    setEndDate(end);
  };

  // Filter data berdasarkan tab aktif
  const getFilteredData = () => {
    if (activeTab === 'peminjaman') {
      return laporan.filter(item => item.tipe_transaksi === 'peminjaman');
    } else if (activeTab === 'pengembalian') {
      return laporan.filter(item => item.tipe_transaksi === 'pengembalian');
    }
    return laporan;
  };

  // Fungsi print
  const handlePrint = () => {
    const printContent = document.getElementById('print-area');
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Laporan ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Perpustakaan SMPN 4 Tilatang Kamang</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px; }
            .header h1 { margin: 0; color: #333; }
            .header h2 { margin: 5px 0; color: #666; }
            .header p { margin: 5px 0; color: #888; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .footer { margin-top: 30px; text-align: right; font-size: 12px; color: #666; }
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>SMPN 4 TILATANG KAMANG</h1>
            <h2>LAPORAN PERPUSTAKAAN ${activeTab.toUpperCase()}</h2>
            <p>Periode: ${new Date(startDate).toLocaleDateString('id-ID')} - ${new Date(endDate).toLocaleDateString('id-ID')}</p>
          </div>
          ${printContent.innerHTML}
          <div class="footer">
            <p>Dicetak pada: ${new Date().toLocaleString('id-ID')}</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Fungsi export CSV
  const handleExport = () => {
    const data = getFilteredData();
    const csvContent = generateCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `laporan_${activeTab}_${startDate}_${endDate}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const generateCSV = (data) => {
    const headers = activeTab === 'peminjaman'
      ? ['No', 'Nama Anggota', 'Judul Buku', 'Tanggal Peminjaman', 'Batas Kembali', 'Status']
      : activeTab === 'pengembalian'
        ? ['No', 'Nama Anggota', 'Judul Buku', 'Tanggal Pengembalian', 'Kondisi Buku', 'Denda', 'Catatan Admin']
        : ['No', 'Tipe', 'Nama Anggota', 'Judul Buku', 'Tanggal', 'Status/Kondisi', 'Denda'];

    const csvRows = [headers.join(',')];

    data.forEach((item, index) => {
      let row;
      if (activeTab === 'peminjaman') {
        row = [
          index + 1,
          `"${item.nama_anggota}"`,
          `"${item.judul_buku}"`,
          new Date(item.tanggal).toLocaleDateString('id-ID'),
          new Date(item.tanggal_kembali).toLocaleDateString('id-ID'),
          item.status_peminjaman || 'Dipinjam'
        ];
      } else if (activeTab === 'pengembalian') {
        row = [
          index + 1,
          `"${item.nama_anggota}"`,
          `"${item.judul_buku}"`,
          item.tanggal_pengembalian ? new Date(item.tanggal_pengembalian).toLocaleDateString('id-ID') : '-',
          item.kondisi_buku || 'baik',
          item.denda > 0 ? formatRupiah(item.denda) : 'Tidak Dikenakan Denda',
          `"${item.catatan_admin || '-'}"`
        ];
      } else {
        row = [
          index + 1,
          item.tipe_transaksi,
          `"${item.nama_anggota}"`,
          `"${item.judul_buku}"`,
          new Date(item.tanggal).toLocaleDateString('id-ID'),
          item.tipe_transaksi === 'peminjaman' ? (item.status_peminjaman || 'Dipinjam') : (item.kondisi_buku || 'baik'),
          item.denda > 0 ? formatRupiah(item.denda) : 'Tidak Dikenakan Denda'
        ];
      }
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  };

  // Fungsi untuk statistik Laporan
  const renderStatistik = () => {
    if (laporan.length === 0) return null;

    const data = getFilteredData();

    if (activeTab === 'peminjaman') {
      const totalPeminjaman = data.length;
      const statusAktif = data.filter(item =>
        (item.status_peminjaman === 'Dipinjam' || item.status_peminjaman === 'Diajukan Pengembalian')
        && item.status_persetujuan === 'approved'
      ).length;
      const statusPending = data.filter(item => item.status_persetujuan === 'pending').length;
      <div className="column">
        <div className="box has-background-light">
          <div className="has-text-centered">
            <p className="title is-3 has-text-primary mb-2">{statusPending}</p>
            <p className="subtitle is-6 has-text-grey-dark mb-1">Menunggu Persetujuan</p>
            <p className="is-size-7 has-text-grey">
              {totalPeminjaman > 0 ? Math.round((statusPending / totalPeminjaman) * 100) : 0}% belum diverifikasi
            </p>
          </div>
        </div>
      </div>
      const statusSelesai = data.filter(item => item.status_peminjaman === 'Selesai').length;

      return (
        <div className="box mt-4">
          <h3 className="title is-5 mb-4 has-text-grey-dark">
            <i className="fas fa-chart-line mr-2"></i>
            Ringkasan Laporan Peminjaman
          </h3>

          <div className="columns is-variable is-4">
            <div className="column">
              <div className="box has-background-light">
                <div className="has-text-centered">
                  <p className="title is-3 has-text-primary mb-2">{totalPeminjaman}</p>
                  <p className="subtitle is-6 has-text-grey-dark mb-1">Total Peminjaman</p>
                  <p className="is-size-7 has-text-grey">transaksi dalam periode</p>
                </div>
              </div>
            </div>

            <div className="column">
              <div className="box has-background-light">
                <div className="has-text-centered">
                  <p className="title is-3 has-text-primary mb-2">{statusAktif}</p>
                  <p className="subtitle is-6 has-text-grey-dark mb-1">Masih Dipinjam</p>
                  <p className="is-size-7 has-text-grey">
                    {totalPeminjaman > 0 ? Math.round((statusAktif / totalPeminjaman) * 100) : 0}% masih aktif
                  </p>
                </div>
              </div>
            </div>

            <div className="column">
              <div className="box has-background-light">
                <div className="has-text-centered">
                  <p className="title is-3 has-text-primary mb-2">{statusSelesai}</p>
                  <p className="subtitle is-6 has-text-grey-dark mb-1">Sudah Selesai</p>
                  <p className="is-size-7 has-text-grey">
                    {totalPeminjaman > 0 ? Math.round((statusSelesai / totalPeminjaman) * 100) : 0}% sudah selesai
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (activeTab === 'pengembalian') {
      const totalPengembalian = data.length;
      const bukuBaik = data.filter(item => (item.kondisi_buku || 'baik') === 'baik').length;
      const bukuBermasalah = data.filter(item => item.kondisi_buku === 'rusak' || item.kondisi_buku === 'hilang').length;
      const totalDenda = data.reduce((sum, item) => sum + (item.denda || 0), 0);

      return (
        <div className="box mt-4">
          <h3 className="title is-5 mb-4 has-text-grey-dark">
            <i className="fas fa-chart-line mr-2"></i>
            Ringkasan Laporan Pengembalian
          </h3>

          <div className="columns is-variable is-4">
            <div className="column">
              <div className="box has-background-light">
                <div className="has-text-centered">
                  <p className="title is-3 has-text-primary mb-2">{totalPengembalian}</p>
                  <p className="subtitle is-6 has-text-grey-dark mb-1">Total Pengembalian</p>
                  <p className="is-size-7 has-text-grey">transaksi dalam periode</p>
                </div>
              </div>
            </div>

            <div className="column">
              <div className="box has-background-light">
                <div className="has-text-centered">
                  <p className="title is-3 has-text-primary mb-2">{bukuBaik}</p>
                  <p className="subtitle is-6 has-text-grey-dark mb-1">Buku Kondisi Baik</p>
                  <p className="is-size-7 has-text-grey">
                    {totalPengembalian > 0 ? Math.round((bukuBaik / totalPengembalian) * 100) : 0}% terawat dengan baik
                  </p>
                </div>
              </div>
            </div>

            <div className="column">
              <div className="box has-background-light">
                <div className="has-text-centered">
                  <p className="title is-3 has-text-primary mb-2">{formatRupiah(totalDenda)}</p>
                  <p className="subtitle is-6 has-text-grey-dark mb-1">Total Denda</p>
                  <p className="is-size-7 has-text-grey">denda keterlambatan</p>
                </div>
              </div>
            </div>
          </div>

          {bukuBermasalah > 0 && (
            <div className="notification is-warning is-light">
              <div className="level">
                <div className="level-left">
                  <div className="level-item">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    <strong>Perhatian:</strong> Ada {bukuBermasalah} buku dalam kondisi rusak/hilang yang perlu ditindaklanjuti.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Gabungan stats (seperti aslinya)
    const peminjamanData = laporan.filter(item => item.tipe_transaksi === 'peminjaman');
    const pengembalianData = laporan.filter(item => item.tipe_transaksi === 'pengembalian');
    const totalPeminjaman = peminjamanData.length;
    const totalPengembalian = pengembalianData.length;
    const bukuBaik = pengembalianData.filter(item => (item.kondisi_buku || 'baik') === 'baik').length;
    const bukuBermasalah = pengembalianData.filter(item => item.kondisi_buku === 'rusak' || item.kondisi_buku === 'hilang').length;
    const totalDenda = pengembalianData.reduce((sum, item) => sum + (item.denda || 0), 0);

    return (
      <div className="box mt-4">
        <h3 className="title is-5 mb-4 has-text-grey-dark">
          <i className="fas fa-chart-line mr-2"></i>
          Ringkasan Laporan
        </h3>

        <div className="columns is-variable is-4">
          <div className="column">
            <div className="box has-background-light">
              <div className="has-text-centered">
                <p className="title is-3 has-text-primary mb-2">{totalPeminjaman}</p>
                <p className="subtitle is-6 has-text-grey-dark mb-1">Total Peminjaman</p>
                <p className="is-size-7 has-text-grey">transaksi dalam periode</p>
              </div>
            </div>
          </div>

          <div className="column">
            <div className="box has-background-light">
              <div className="has-text-centered">
                <p className="title is-3 has-text-primary mb-2">{totalPengembalian}</p>
                <p className="subtitle is-6 has-text-grey-dark mb-1">Total Pengembalian</p>
                <p className="is-size-7 has-text-grey">
                  {totalPeminjaman > 0 ? Math.round((totalPengembalian / totalPeminjaman) * 100) : 0}% dari peminjaman
                </p>
              </div>
            </div>
          </div>

          <div className="column">
            <div className="box has-background-light">
              <div className="has-text-centered">
                <p className="title is-3 has-text-primary mb-2">{bukuBaik}</p>
                <p className="subtitle is-6 has-text-grey-dark mb-1">Buku Kondisi Baik</p>
                <p className="is-size-7 has-text-grey">
                  {totalPengembalian > 0 ? Math.round((bukuBaik / totalPengembalian) * 100) : 0}% terawat dengan baik
                </p>
              </div>
            </div>
          </div>

          <div className="column">
            <div className="box has-background-light">
              <div className="has-text-centered">
                <p className="title is-3 has-text-primary mb-2">{formatRupiah(totalDenda)}</p>
                <p className="subtitle is-6 has-text-grey-dark mb-1">Total Denda</p>
                <p className="is-size-7 has-text-grey">denda keterlambatan</p>
              </div>
            </div>
          </div>
        </div>

        {bukuBermasalah > 0 && (
          <div className="notification is-warning is-light">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  <strong>Perhatian:</strong> Ada {bukuBermasalah} buku dalam kondisi rusak/hilang yang perlu ditindaklanjuti.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderTable = () => {
    if (loading) {
      return <p className="has-text-centered">Memuat data laporan...</p>;
    }

    const data = getFilteredData();

    if (data.length === 0) {
      return <p className="has-text-centered">Tidak ada data laporan dalam rentang tanggal yang dipilih.</p>;
    }

    // Pagination logic
    const totalEntries = data.length;
    const totalPages = Math.ceil(totalEntries / entriesPerPage);
    const startIndex = (currentPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    const currentEntries = data.slice(startIndex, endIndex);

    return (
      <div className="mt-4">
        {/* Kontrol Entries dan Info */}
        <div className="level mb-4">
          <div className="level-left">
            <div className="level-item">
              <div className="field has-addons">
                <p className="control">
                  <span className="select">
                    <select value={entriesPerPage} onChange={(e) => {
                      setEntriesPerPage(parseInt(e.target.value));
                      setCurrentPage(1);
                    }}>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </span>
                </p>
                <p className="control">
                  <span className="button is-static">entri per halaman</span>
                </p>
              </div>
            </div>
            <div className="level-item">
              <span className="has-text-grey">
                Menampilkan {startIndex + 1} sampai {Math.min(endIndex, totalEntries)} dari {totalEntries} entri
              </span>
            </div>
          </div>

          {/* Tombol Print dan Export */}
          <div className="level-right no-print">
            <div className="level-item">
              <div className="buttons">
                <button className="button is-info" onClick={handlePrint}>
                  <span className="icon is-small">
                    <i className="fas fa-print"></i>
                  </span>
                  <span>Cetak</span>
                </button>
                <button className="button is-success" onClick={handleExport}>
                  <span className="icon is-small">
                    <i className="fas fa-download"></i>
                  </span>
                  <span>Export CSV</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Print Area */}
        <div id="print-area">
          <table className="table is-fullwidth is-striped is-hoverable">
            <thead>
              <tr>
                <th>No</th>
                {activeTab === 'peminjaman' && (
                  <>
                    <th>Nama Anggota</th>
                    <th>Judul Buku</th>
                    <th>Tanggal Peminjaman</th>
                    <th>Batas Kembali</th>
                    <th>Status</th>
                  </>
                )}
                {activeTab === 'pengembalian' && (
                  <>
                    <th>Nama Anggota</th>
                    <th>Judul Buku</th>
                    <th>Tanggal Pengembalian</th>
                    <th>Kondisi Buku</th>
                    <th>Denda</th>
                    <th>Catatan</th>
                  </>
                )}
                {activeTab === 'gabungan' && (
                  <>
                    <th>Tipe Transaksi</th>
                    <th>Nama Anggota</th>
                    <th>Judul Buku</th>
                    <th>Tanggal Peminjaman</th>
                    <th>Tanggal Pengembalian</th>
                    <th>Kondisi Buku</th>
                    <th>Denda</th>
                    <th>Catatan</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {currentEntries.map((item, index) => (
                <tr key={index}>
                  <td>{startIndex + index + 1}</td>

                  {activeTab === 'peminjaman' && (
                    <>
                      <td>{item.nama_anggota}</td>
                      <td>{item.judul_buku}</td>
                      <td>{new Date(item.tanggal).toLocaleDateString('id-ID')}</td>
                      <td>{new Date(item.tanggal_kembali).toLocaleDateString('id-ID')}</td>
                      <td>
                        <span className={`tag ${item.status_persetujuan === 'pending' ? 'is-warning' :
                          item.status_persetujuan === 'rejected' ? 'is-danger' :
                            item.status_persetujuan === 'expired' ? 'is-dark' :
                              item.status_peminjaman === 'Dipinjam' ? 'is-info' :
                                item.status_peminjaman === 'Selesai' ? 'is-success' :
                                  item.status_peminjaman === 'Diajukan Pengembalian' ? 'is-link' : 'is-light'
                          }`}>
                          {item.status_persetujuan === 'pending' ? '⏳ Menunggu Persetujuan' :
                            item.status_persetujuan === 'rejected' ? '❌ Ditolak' :
                              item.status_persetujuan === 'expired' ? '⌛ Expired' :
                                item.status_peminjaman || 'Dipinjam'}
                        </span>
                        {item.status_persetujuan === 'rejected' && item.keterangan_admin && (
                          <p className="help is-danger is-size-7 mt-1" title={item.keterangan_admin}>
                            <i className="fas fa-info-circle mr-1"></i>
                            {item.keterangan_admin.length > 40
                              ? item.keterangan_admin.substring(0, 40) + '...'
                              : item.keterangan_admin}
                          </p>
                        )}
                      </td>
                    </>
                  )}

                  {activeTab === 'pengembalian' && (
                    <>
                      <td>{item.nama_anggota}</td>
                      <td>{item.judul_buku}</td>
                      <td>{item.tanggal_pengembalian ? new Date(item.tanggal_pengembalian).toLocaleDateString('id-ID') : '-'}</td>
                      <td>
                        <span className={`tag ${item.kondisi_buku === 'baik' ? 'is-success' :
                          item.kondisi_buku === 'rusak' ? 'is-warning' :
                            item.kondisi_buku === 'hilang' ? 'is-danger' : 'is-success'
                          }`}>
                          {item.kondisi_buku || 'baik'}
                        </span>
                      </td>
                      <td>
                        {item.denda !== null && item.denda !== undefined && item.denda > 0
                          ? formatRupiah(item.denda)
                          : 'Tidak Dikenakan Denda'}
                      </td>
                      <td>
                        {item.catatan_admin ? (
                          <div className="has-text-grey-dark">
                            <small>
                              <i className="fas fa-sticky-note mr-1"></i>
                              {item.catatan_admin.length > 30
                                ? `${item.catatan_admin.substring(0, 30)}...`
                                : item.catatan_admin
                              }
                            </small>
                          </div>
                        ) : (
                          <span className="has-text-grey">-</span>
                        )}
                      </td>
                    </>
                  )}

                  {activeTab === 'gabungan' && (
                    <>
                      <td>
                        <span className={`tag ${item.tipe_transaksi === 'peminjaman' ? 'is-info' : 'is-success'}`}>
                          {item.tipe_transaksi}
                        </span>
                      </td>
                      <td>{item.nama_anggota}</td>
                      <td>{item.judul_buku}</td>
                      <td>{new Date(item.tanggal).toLocaleDateString('id-ID')}</td>
                      <td>{item.tanggal_pengembalian ? new Date(item.tanggal_pengembalian).toLocaleDateString('id-ID') : '-'}</td>
                      <td>
                        {item.tipe_transaksi === 'pengembalian' ? (
                          <span className={`tag ${item.kondisi_buku === 'baik' ? 'is-success' :
                            item.kondisi_buku === 'rusak' ? 'is-warning' :
                              item.kondisi_buku === 'hilang' ? 'is-danger' : 'is-light'
                            }`}>
                            {item.kondisi_buku || 'baik'}
                          </span>
                        ) : (
                          <span className="tag is-light">-</span>
                        )}
                      </td>
                      <td>
                        {item.denda !== null && item.denda !== undefined && item.denda > 0
                          ? formatRupiah(item.denda)
                          : 'Tidak Dikenakan Denda'}
                      </td>
                      <td>
                        {item.tipe_transaksi === 'pengembalian' && item.catatan_admin ? (
                          <div className="has-text-grey-dark">
                            <small>
                              <i className="fas fa-sticky-note mr-1"></i>
                              {item.catatan_admin.length > 30
                                ? `${item.catatan_admin.substring(0, 30)}...`
                                : item.catatan_admin
                              }
                            </small>
                          </div>
                        ) : (
                          <span className="has-text-grey">-</span>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="pagination is-centered mt-4 no-print" role="navigation" aria-label="pagination">
            <a className={`pagination-previous ${currentPage === 1 ? 'is-disabled' : ''}`}
              onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
              style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
              Previous
            </a>
            <a className={`pagination-next ${currentPage === totalPages ? 'is-disabled' : ''}`}
              onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
              style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
              Next
            </a>
            <ul className="pagination-list">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }

                return (
                  <li key={page}>
                    <a className={`pagination-link ${page === currentPage ? 'is-current' : ''}`}
                      onClick={() => setCurrentPage(page)}
                      style={{ cursor: 'pointer' }}>
                      {page}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </div>
    );
  };

  return (
    <Layout title="Laporan">
      <h2 className="title is-4">Halaman Laporan Peminjaman & Pengembalian</h2>

      {/* Filter Controls - tetap seperti aslinya */}
      <div className="box">
        <div className="field is-grouped is-grouped-multiline">
          <div className="control">
            <label className="label">Tanggal Awal</label>
            <input
              className="input"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setFilterType('custom');
              }}
            />
          </div>
          <div className="control">
            <label className="label">Tanggal Akhir</label>
            <input
              className="input"
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setFilterType('custom');
              }}
            />
          </div>
          <div className="control is-flex is-align-items-flex-end">
            <button className="button is-info" onClick={fetchLaporan}>
              Tampilkan Laporan
            </button>
          </div>
        </div>

        <div className="buttons mt-4">
          <button
            className={`button ${filterType === 'daily' ? 'is-info is-selected' : ''}`}
            onClick={() => handleFilterChange('daily')}
          >
            Hari Ini
          </button>
          <button
            className={`button ${filterType === 'monthly' ? 'is-info is-selected' : ''}`}
            onClick={() => handleFilterChange('monthly')}
          >
            Bulan Ini
          </button>
          <button
            className={`button ${filterType === 'yearly' ? 'is-info is-selected' : ''}`}
            onClick={() => handleFilterChange('yearly')}
          >
            Tahun Ini
          </button>
        </div>
      </div>

      {/* Tab Navigation - Fitur Baru */}
      {laporan.length > 0 && (
        <div className="box">
          <div className="tabs is-boxed">
            <ul>
              <li className={activeTab === 'gabungan' ? 'is-active' : ''}>
                <a onClick={() => {
                  setActiveTab('gabungan');
                  setCurrentPage(1);
                }}>
                  <span className="icon is-small">
                    <i className="fas fa-chart-bar"></i>
                  </span>
                  <span>Laporan Gabungan</span>
                </a>
              </li>
              <li className={activeTab === 'peminjaman' ? 'is-active' : ''}>
                <a onClick={() => {
                  setActiveTab('peminjaman');
                  setCurrentPage(1);
                }}>
                  <span className="icon is-small">
                    <i className="fas fa-book"></i>
                  </span>
                  <span>Peminjaman</span>
                </a>
              </li>
              <li className={activeTab === 'pengembalian' ? 'is-active' : ''}>
                <a onClick={() => {
                  setActiveTab('pengembalian');
                  setCurrentPage(1);
                }}>
                  <span className="icon is-small">
                    <i className="fas fa-undo-alt"></i>
                  </span>
                  <span>Pengembalian</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}

      {/* Tampilkan statistik jika ada data */}
      {renderStatistik()}

      {/* Tampilkan tabel laporan */}
      {renderTable()}

      {/* CSS untuk print dan tab styling */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          .box {
            box-shadow: none !important;
            border: none !important;
          }
          .table th,
          .table td {
            border: 1px solid #333 !important;
            font-size: 12px !important;
            padding: 6px !important;
          }
          .table th {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
          }
          .tag {
            background-color: #f5f5f5 !important;
            color: #333 !important;
            border: 1px solid #ddd !important;
          }
        }
        
        .tabs a {
          cursor: pointer;
        }
        
        .tabs a:hover {
          border-bottom-color: #3273dc;
          color: #3273dc;
        }
      `}</style>
    </Layout>
  );
};

export default Laporan;